import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  confirmPasswordReset,
  getAuth,
  onAuthStateChanged,
  verifyPasswordResetCode,
} from "firebase/auth";
// Components
import Loading from "../../components/Loading";
// Styles
import "../../styles/pages/ResetPassword.css";

function ResetPassword() {
  // URL and navigation
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Firebase authentication instance
  const auth = getAuth();
  // Firebase password reset code from the email link
  const oobCode = searchParams.get("oobCode");
  // Page state
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [status, setStatus] = useState("checking");
  const [resetEmail, setResetEmail] = useState("");
  // Password form state
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // Request state
  const [error, setError] = useState("");
  const [isPending, setIsPending] = useState(false);

  // Redirect authenticated users away from the password reset page
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        navigate("/", { replace: true });
        return;
      }

      setCheckingAuth(false);
    });

    return unsubscribe;
  }, [auth, navigate]);

  // Validate the Firebase password reset code after authentication is checked
  useEffect(() => {
    if (checkingAuth) return;
  
    if (!oobCode) {
      setStatus("invalid");
      return;
    }

    let active = true;

    const validateResetCode = async () => {
      setStatus("checking");

      try {
        // Firebase returns the email address associated with the reset request
        const email = await verifyPasswordResetCode(auth, oobCode);

        if (!active) return;

        setResetEmail(email);
        setStatus("valid");
      } catch (err) {
        if (active) setStatus("invalid");
      }
    };

    validateResetCode();

    return () => {
      active = false;
    };
  }, [checkingAuth, oobCode, auth]);

  // Redirect to login shortly after a successful password reset
  useEffect(() => {
    if (status !== "success") return;

    const redirectTimer = window.setTimeout(() => {
      navigate("/login", { replace: true });
    }, 2500);

    return () => window.clearTimeout(redirectTimer);
  }, [status, navigate]);

  // Submit the new password to Firebase
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isPending || !oobCode) return;

    setError("");

    // Firebase passwords must contain at least 6 characters
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    // Make sure both password fields match
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setIsPending(true);

    try {
      await confirmPasswordReset(auth, oobCode, password);

      // Clear password values from component state
      setPassword("");
      setConfirm("");

      setStatus("success");
    } catch (err) {
      // The code may have expired between validation and submission
      if (err?.code === "auth/expired-action-code" || err?.code === "auth/invalid-action-code") {
        setStatus("invalid");
        return;
      }

      if (err?.code === "auth/weak-password") {
        setError("Please choose a stronger password.");
        return;
      }

      setError("Something went wrong while resetting your password. Please try again.");
    } finally {
      setIsPending(false);
    }
  };

  // Show the shared loading screen while Firebase checks authentication
  if (checkingAuth) return <Loading message="Checking authentication" />;

  return (
    <main className="reset-password-page position-relative d-flex align-items-center overflow-hidden">
      {/* Subtle background grid */}
      <div
        className="reset-password-grid position-absolute top-0 start-0 w-100 h-100"
        aria-hidden="true"
      />
      {/* Decorative Benoma background text */}
      <div
        className="reset-password-background-text position-absolute user-select-none"
        aria-hidden="true"
      >
        RESET
      </div>
      <div className="container position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-12 col-md-8 col-lg-6 col-xl-5">
            {/* Back navigation */}
            <Link
              to="/login"
              className="reset-password-back d-inline-flex align-items-center gap-2 text-decoration-none mb-4"
            >
              <i className="bi bi-arrow-left" aria-hidden="true" />
              Back to login
            </Link>
            <div className="reset-password-card">
              {/* Page header */}
              <div className="reset-password-card-header">
                <div className="d-flex align-items-center justify-content-between gap-3 mb-4">
                  <span className="reset-password-label font-monospace text-danger">
                    ACCOUNT RECOVERY
                  </span>
                  <span className="reset-password-status d-flex align-items-center gap-2 font-monospace">
                    <span
                      className={`reset-password-status-dot rounded-circle ${
                        status === "invalid" ? "reset-password-status-dot-invalid" : ""
                      }`}
                    />
                    {status === "checking" ? "verifying" : "secure"}
                  </span>
                </div>
                <h1 className="reset-password-title fw-bold mb-3">
                  {status === "success" ? "Password updated." : "Choose a new password."}
                </h1>
                <p className="reset-password-description text-secondary mb-0">
                  {status === "success"
                    ? "Your Benoma account password has been successfully changed."
                    : "Create a new password for your Benoma account."}
                </p>
              </div>
              {/* Card content */}
              <div className="reset-password-card-body">
                {/* Checking reset link */}
                {status === "checking" && (
                  <div
                    className="reset-password-checking text-center py-4"
                    role="status"
                    aria-live="polite"
                  >
                    <span
                      className="spinner-border spinner-border-sm text-danger mb-3"
                      aria-hidden="true"
                    />
                    <span className="d-block fw-semibold mb-2">Verifying reset link</span>
                    <small className="text-secondary">
                      Please wait while we verify your password reset request.
                    </small>
                  </div>
                )}
                {/* Invalid or expired reset link */}
                {status === "invalid" && (
                  <div className="reset-password-state text-center" role="alert">
                    <div className="reset-password-state-icon mx-auto mb-4">
                      <i className="bi bi-link-45deg" aria-hidden="true" />
                    </div>
                    <h2 className="reset-password-state-title mb-3">Reset link unavailable</h2>
                    <p className="text-secondary mb-4">
                      This password reset link is invalid, has expired, or has already been used.
                    </p>
                    <Link
                      to="/forgot-password"
                      className="btn btn-danger reset-password-primary-action w-100 d-flex align-items-center justify-content-center gap-2"
                    >
                      Request New Link
                      <i className="bi bi-arrow-right" aria-hidden="true" />
                    </Link>
                  </div>
                )}
                {/* Valid reset link */}
                {status === "valid" && (
                  <>
                    {/* Account being recovered */}
                    {resetEmail && (
                      <div className="reset-password-account d-flex align-items-center gap-3 mb-4">
                        <div className="reset-password-account-icon d-flex align-items-center justify-content-center">
                          <i className="bi bi-person" aria-hidden="true" />
                        </div>
                        <div className="min-w-0">
                          <span className="reset-password-account-label font-monospace d-block">
                            RESETTING PASSWORD FOR
                          </span>
                          <span className="reset-password-account-email d-block text-truncate">
                            {resetEmail}
                          </span>
                        </div>
                      </div>
                    )}
                    {/* Password validation error */}
                    {error && (
                      <div
                        className="reset-password-alert d-flex align-items-start gap-3 mb-4"
                        role="alert"
                      >
                        <i className="bi bi-exclamation-circle text-danger" aria-hidden="true" />
                        <div>
                          <span className="d-block fw-semibold mb-1">Unable to reset password</span>
                          <small>{error}</small>
                        </div>
                      </div>
                    )}
                    <form onSubmit={handleSubmit}>
                      {/* New password */}
                      <div className="mb-4">
                        <label
                          htmlFor="new-password"
                          className="reset-password-form-label form-label"
                        >
                          New password
                        </label>
                        <div className="reset-password-input-wrapper position-relative">
                          <i
                            className="bi bi-lock reset-password-input-icon position-absolute"
                            aria-hidden="true"
                          />
                          <input
                            type={showPassword ? "text" : "password"}
                            id="new-password"
                            className="form-control reset-password-input"
                            placeholder="Enter a new password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="new-password"
                            disabled={isPending}
                            minLength={6}
                            required
                          />
                          <button
                            type="button"
                            className="reset-password-toggle position-absolute"
                            onClick={() => setShowPassword((current) => !current)}
                            disabled={isPending}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            aria-pressed={showPassword}
                          >
                            <i
                              className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                        <small className="reset-password-help d-block mt-2">
                          Minimum 6 characters.
                        </small>
                      </div>
                      {/* Confirm password */}
                      <div className="mb-4">
                        <label
                          htmlFor="confirm-password"
                          className="reset-password-form-label form-label"
                        >
                          Confirm new password
                        </label>
                        <div className="reset-password-input-wrapper position-relative">
                          <i
                            className="bi bi-shield-lock reset-password-input-icon position-absolute"
                            aria-hidden="true"
                          />
                          <input
                            type={showConfirm ? "text" : "password"}
                            id="confirm-password"
                            className="form-control reset-password-input"
                            placeholder="Enter your new password again"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            autoComplete="new-password"
                            disabled={isPending}
                            minLength={6}
                            required
                          />
                          <button
                            type="button"
                            className="reset-password-toggle position-absolute"
                            onClick={() => setShowConfirm((current) => !current)}
                            disabled={isPending}
                            aria-label={
                              showConfirm
                                ? "Hide confirmation password"
                                : "Show confirmation password"
                            }
                            aria-pressed={showConfirm}
                          >
                            <i
                              className={`bi ${showConfirm ? "bi-eye-slash" : "bi-eye"}`}
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </div>
                      {/* Submit */}
                      <button
                        type="submit"
                        className="btn btn-danger reset-password-submit w-100 d-flex align-items-center justify-content-center gap-2"
                        disabled={isPending}
                      >
                        {isPending ? (
                          <>
                            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                            <span>Updating password...</span>
                          </>
                        ) : (
                          <>
                            <span>Reset Password</span>
                            <i className="bi bi-arrow-right" aria-hidden="true" />
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
                {/* Successful reset */}
                {status === "success" && (
                  <div
                    className="reset-password-state text-center"
                    role="status"
                    aria-live="polite"
                  >
                    <div className="reset-password-state-icon reset-password-success-icon mx-auto mb-4">
                      <i className="bi bi-check-lg" aria-hidden="true" />
                    </div>
                    <h2 className="reset-password-state-title mb-3">You're all set.</h2>
                    <p className="text-secondary mb-4">
                      Your password has been updated. You can now sign in using your new password.
                    </p>
                    <Link
                      to="/login"
                      className="btn btn-danger reset-password-primary-action w-100 d-flex align-items-center justify-content-center gap-2"
                    >
                      Continue to Login
                      <i className="bi bi-arrow-right" aria-hidden="true" />
                    </Link>
                    <small className="reset-password-redirect d-block mt-3 font-monospace">
                      Redirecting automatically...
                    </small>
                  </div>
                )}
              </div>
              {/* Terminal-style footer */}
              <div className="reset-password-card-footer font-monospace">
                <span className="text-danger">›</span>
                <span className="text-secondary">
                  {status === "success"
                    ? " password updated"
                    : status === "invalid"
                      ? " reset link invalid"
                      : " password recovery"}
                </span>
                <span className="reset-password-terminal-cursor" aria-hidden="true">
                  _
                </span>
              </div>
            </div>
            {/* Portal label */}
            <p className="reset-password-footer-text text-center text-secondary font-monospace mt-4 mb-0">
              BENOMA / ACCOUNT RECOVERY
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}

export default ResetPassword;
